import React, { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, useTheme } from '@mui/material';
import { Header, Row } from '../../../../components/Table/TableModels';
import UserDetailsContext from '../../../../contexts/UserDetailsContext/UserDetailsContext';
import { Sort } from '../../../../models/CommonModels';
import { Device } from '../../../../models/UserDevicesModels';
import UserDetailsDeviceRowOptions from './UserDetailsDeviceRowOptions';
import TruncatedTooltip from '../../../../components/TruncatedTooltip/TruncatedTooltip';

function useUserDetailsDevicesTable(
  devices: Device[],
  sort: Sort,
  resendNotification: (deviceId: string) => void,
  isPerformingAction: boolean,
  deleteDevice: (deviceId: string) => void,
) {
  const [t] = useTranslation();
  const user = useContext(UserDetailsContext);
  const theme = useTheme();

  /**
   * Sorts the array of devices
   */
  const sortBy = useCallback(
    (devs: Device[]): Device[] => {
      if (devs.length) {
        if (!Object.keys(devs[0]).includes(sort.orderBy)) {
          throw new Error(`Invalid field name: ${sort.orderBy}`);
        }
        type SortableKeys = Extract<'manufacturer' | 'model' | 'eid' | 'deviceType', keyof Device>;
        return devs.sort((a, b) => {
          let comparison: number;
          try {
            comparison = a[sort.orderBy as SortableKeys].localeCompare(b[sort.orderBy as SortableKeys]);
          } catch (e) {
            comparison = 1;
          }
          return sort.order === 'desc' ? -comparison : comparison;
        });
      }
      return [];
    },
    [sort.order, sort.orderBy],
  );

  const headers: Header[] = useMemo(
    () => [
      {
        id: 'manufacturer',
        label: t('pages.userDetails.tabs.devicesTable.headers.manufacturer'),
        isSortable: true,
      },
      {
        id: 'model',
        label: t('pages.userDetails.tabs.devicesTable.headers.device'),
        isSortable: true,
      },
      {
        id: 'eid',
        label: t('pages.userDetails.tabs.devicesTable.headers.simId'),
        isSortable: true,
      },
      {
        id: 'phoneNumber',
        label: t('pages.userDetails.tabs.devicesTable.headers.mobileNumber'),
      },
      {
        id: 'simType',
        label: t('pages.userDetails.tabs.devicesTable.headers.simType'),
      },
      {
        id: 'deviceType',
        label: t('pages.userDetails.tabs.devicesTable.headers.deviceType'),
        isSortable: true,
      },
      {
        id: 'details',
        label: '',
        sx: {
          position: 'sticky',
          right: 0,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: 1,
            boxShadow: '-8px 0px 16px 0px #00000029',
            opacity: 'var(--sticky-actions-shadow, 0)',
            pointerEvents: 'none',
            transition: 'opacity 120ms ease',
          },
        },
      },
    ],
    [t],
  );

  const rows: Row[] = useMemo(
    () =>
      sortBy(devices).map((d, index) => ({
        id: d.id,
        cells: [
          {
            value: d.manufacturer,
          },
          {
            value: d.model,
          },
          {
            value: d.simId ? <TruncatedTooltip label={d.simId} limit={4} /> : t('common.na'),
          },
          {
            value: d.phoneNumber,
          },
          {
            value: d.simType ? t(`common.simType.${d.simType}`) : t('common.na'),
          },
          {
            value: d.deviceType ? t(`common.deviceType.${d.deviceType}`) : t('common.na'),
          },
          {
            value: (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  minWidth: '3.25rem',
                }}
              >
                <UserDetailsDeviceRowOptions
                  device={d}
                  deleteDevice={deleteDevice}
                  isPerformingAction={isPerformingAction}
                />
              </Box>
            ),
            sx: {
              position: 'sticky',
              right: 0,
              p: 0,
              backgroundColor: index % 2 !== 0 ? theme.palette.monochrome.monoChrome2 : 'white',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                width: 1,
                boxShadow: '-8px 0px 16px 0px #00000029',
                opacity: 'var(--sticky-actions-shadow, 0)',
                pointerEvents: 'none',
                transition: 'opacity 120ms ease',
              },
            },
          },
        ],
      })),
    [devices, sortBy, t, isPerformingAction, user],
  );

  return { headers, rows, totalDevices: devices.length };
}

export default useUserDetailsDevicesTable;
